import {useState, useEffect, useRef, useContext} from "react";
import {sendSlackMessageTracked} from "../../models/notifications";
import { tiers } from "../shop/retainer/retainerPricingComponent";
import {DashboardContext} from "../../context/DashboardContext";

// ── Tiny helpers ───────────────────────────────────────────────────────────────
function cn(...cs) { return cs.filter(Boolean).join(" "); }

// ── Step config ────────────────────────────────────────────────────────────────
const STEPS = ["welcome", "about", "website", "goals", "plan", "team", "ready"];

// ── Progress dots ──────────────────────────────────────────────────────────────
function ProgressDots({ step }) {
    const inner = STEPS.slice(1, -1);
    return (
        <div className="flex items-center gap-2">
            {inner.map((s, i) => {
                const idx = i + 1;
                const cur = STEPS.indexOf(step);
                const done = cur > idx;
                const active = cur === idx;
                return (
                    <div key={s} className={cn(
                        "transition-all duration-500 rounded-full",
                        active ? "w-6 h-2 bg-orange-500" : done ? "w-2 h-2 bg-orange-300" : "w-2 h-2 bg-gray-200"
                    )}/>
                );
            })}
        </div>
    );
}

// ── Floating label input ───────────────────────────────────────────────────────
function FloatingInput({ label, type = "text", value, onChange, placeholder, autoFocus }) {
    const [focused, setFocused] = useState(false);
    const lifted = focused || value;
    return (
        <div className="relative">
            <label className={cn(
                "absolute left-4 transition-all duration-200 pointer-events-none select-none",
                lifted ? "top-2 text-[11px] font-semibold tracking-wider uppercase text-orange-500" : "top-4 text-sm text-gray-400"
            )}>{label}</label>
            <input
                type={type}
                autoFocus={autoFocus}
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={focused ? placeholder : ""}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                className="w-full border border-gray-200 rounded-xl px-4 pt-7 pb-3 text-sm text-gray-900 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 bg-white transition-all"
            />
        </div>
    );
}

// ── Pill select ────────────────────────────────────────────────────────────────
function PillSelect({ options, value, onChange, multi = false }) {
    const toggle = (opt) => {
        if (!multi) { onChange(opt); return; }
        const arr = Array.isArray(value) ? value : [];
        onChange(arr.includes(opt) ? arr.filter(v => v !== opt) : [...arr, opt]);
    };
    const isSelected = (opt) => multi ? (Array.isArray(value) && value.includes(opt)) : value === opt;
    return (
        <div className="flex flex-wrap gap-2">
            {options.map(opt => (
                <button key={opt} onClick={() => toggle(opt)}
                        className={cn(
                            "px-4 py-2 rounded-full text-sm border transition-all duration-200 font-medium",
                            isSelected(opt)
                                ? "bg-orange-500 border-orange-500 text-white shadow-sm"
                                : "bg-white border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600"
                        )}>
                    {opt}
                </button>
            ))}
        </div>
    );
}

// ── Card selector ──────────────────────────────────────────────────────────────
function CardSelect({ options, value, onChange, multi = false }) {
    const toggle = (opt) => {
        if (!multi) { onChange(opt.id); return; }
        const arr = Array.isArray(value) ? value : [];
        onChange(arr.includes(opt.id) ? arr.filter(v => v !== opt.id) : [...arr, opt.id]);
    };
    const isSelected = (id) => multi ? (Array.isArray(value) && value.includes(id)) : value === id;
    return (
        <div className="grid grid-cols-2 gap-3">
            {options.map(opt => (
                <button key={opt.id} onClick={() => toggle(opt)}
                        className={cn(
                            "p-4 rounded-2xl border-2 text-left transition-all duration-200",
                            isSelected(opt.id)
                                ? "border-orange-500 bg-orange-50 shadow-sm"
                                : "border-gray-100 bg-white hover:border-orange-200"
                        )}>
                    <div className="text-2xl mb-2">{opt.icon}</div>
                    <div className={cn("font-semibold text-sm", isSelected(opt.id) ? "text-orange-700" : "text-gray-800")}>{opt.label}</div>
                    {opt.desc && <div className="text-xs text-gray-400 mt-0.5">{opt.desc}</div>}
                </button>
            ))}
        </div>
    );
}

// ── STEP: Welcome ──────────────────────────────────────────────────────────────
function StepWelcome({ onNext }) {
    return (
        <div className="flex flex-col items-center text-center">
            {/* Animated logo mark */}
            <div className="relative mb-8">
                <div className="w-20 h-20 rounded-3xl bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-200 animate-[bounce_2s_ease-in-out_infinite]">
                    <svg width="36" height="36" viewBox="0 0 100 100" fill="white">
                        <path d="M78 67c0 0-1-5-9-7l-37-7c-1 0-1-1 0-1l37-7c8-2 9-7 9-7s1-7-7-10L29 12c-6-2-13 0-16 6L5 45c-3 6-1 13 5 16l18 8v5c0 7 5 12 12 12h38c8 0 12-7 9-13z"/>
                    </svg>
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-green-400 border-2 border-white flex items-center justify-center">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><polyline points="20 6 9 17 4 12" strokeWidth="3" stroke="white" fill="none" strokeLinecap="round"/></svg>
                </div>
            </div>

            <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-600 text-xs font-semibold px-3 py-1.5 rounded-full mb-5 border border-orange-100">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block"></span>
                Welcome to HerStory SEO
            </div>

            <h1 className="text-4xl font-bold text-gray-900 mb-4 leading-tight" style={{fontFamily:"'Georgia',serif"}}>
                Your story deserves<br/>to be found.
            </h1>
            <p className="text-gray-500 text-base max-w-sm leading-relaxed mb-8">
                You've made a powerful investment. In the next 2 minutes, we'll set up your account so we can start growing your organic presence.
            </p>

            {/* Social proof */}
            <div className="flex items-center gap-3 mb-10">
                <div className="flex -space-x-2">
                    {["#C17B8A","#5A7A5C","#B08968","#6B7F6E"].map((c,i) => (
                        <div key={i} className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-bold" style={{background:c}}>
                            {["S","M","J","A"][i]}
                        </div>
                    ))}
                </div>
                <div className="text-left">
                    <div className="text-sm font-semibold text-gray-800">Join 240+ founders</div>
                    <div className="text-xs text-gray-400">growing organically with us</div>
                </div>
            </div>

            <button onClick={onNext}
                    className="w-full max-w-xs py-4 rounded-2xl bg-orange-500 text-white font-semibold text-base hover:bg-orange-600 active:scale-95 transition-all shadow-lg shadow-orange-200">
                Let's get started →
            </button>
            <p className="text-xs text-gray-400 mt-4">Takes about 2 minutes · No credit card required</p>
        </div>
    );
}

// ── STEP: About You ────────────────────────────────────────────────────────────
function StepAbout({ data, setData, onNext, onBack }) {
    const ok = data.firstName && data.lastName && data.email && data.biz;
    return (
        <div>
            <div className="mb-8">
                <div className="text-orange-500 text-sm font-semibold mb-2">Step 1 of 5</div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{fontFamily:"'Georgia',serif"}}>Nice to meet you.</h2>
                <p className="text-gray-500 text-sm">Let's start with the basics so we can personalize your experience.</p>
            </div>

            <div className="space-y-4 mb-8">
                <div className="grid grid-cols-2 gap-3">
                    <FloatingInput label="First name" value={data.firstName} onChange={v => setData({...data, firstName:v})} autoFocus />
                    <FloatingInput label="Last name" value={data.lastName} onChange={v => setData({...data, lastName:v})} />
                </div>
                <FloatingInput label="Work email" type="email" value={data.email} onChange={v => setData({...data, email:v})} placeholder="you@company.com" />
                <FloatingInput label="Business name" value={data.biz} onChange={v => setData({...data, biz:v})} placeholder="Your company or brand" />

                <div>
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-2">Industry</label>
                    <PillSelect
                        options={["E-commerce","Professional Services","SaaS","Healthcare","Real Estate","Creative / Agency","Other"]}
                        value={data.industry}
                        onChange={v => setData({...data, industry:v})}
                    />
                </div>
            </div>

            <div className="flex gap-3">
                <button onClick={onBack} className="px-5 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-all">Back</button>
                <button onClick={onNext} disabled={!ok}
                        className={cn("flex-1 py-3 rounded-xl text-white font-semibold text-sm transition-all",
                            ok ? "bg-orange-500 hover:bg-orange-600 active:scale-95" : "bg-gray-200 text-gray-400 cursor-not-allowed")}>
                    Continue
                </button>
            </div>
        </div>
    );
}

// ── STEP: Website ──────────────────────────────────────────────────────────────
function StepWebsite({ data, setData, onNext, onBack }) {
    const [adding, setAdding] = useState(false);
    const [newDomain, setNewDomain] = useState("");

    const addDomain = () => {
        const clean = newDomain.replace(/^https?:\/\//, "").replace(/\/$/, "");
        if (clean && !data.domains.includes(clean)) {
            setData({...data, domains: [...data.domains, clean]});
        }
        setNewDomain(""); setAdding(false);
    };

    const remove = (d) => setData({...data, domains: data.domains.filter(x => x !== d)});

    return (
        <div>
            <div className="mb-8">
                <div className="text-orange-500 text-sm font-semibold mb-2">Step 2 of 5</div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{fontFamily:"'Georgia',serif"}}>Add your website{data.domains.length !== 1 ? "s" : ""}.</h2>
                <p className="text-gray-500 text-sm">We'll connect them to your dashboard and start monitoring immediately.</p>
            </div>

            {/* Domain list */}
            <div className="space-y-2 mb-4">
                {data.domains.map(d => (
                    <div key={d} className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-xl">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" strokeLinecap="round"/></svg>
                        </div>
                        <span className="text-sm text-gray-800 font-medium flex-1">{d}</span>
                        <button onClick={() => remove(d)} className="text-gray-300 hover:text-red-400 transition-colors">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                    </div>
                ))}

                {adding ? (
                    <div className="flex gap-2">
                        <input autoFocus value={newDomain} onChange={e => setNewDomain(e.target.value)}
                               onKeyDown={e => e.key === "Enter" && addDomain()}
                               placeholder="yourdomain.com"
                               className="flex-1 border border-orange-300 rounded-xl px-4 py-3 text-sm outline-none ring-2 ring-orange-100 focus:ring-orange-200"/>
                        <button onClick={addDomain} className="px-4 py-3 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600">Add</button>
                        <button onClick={() => setAdding(false)} className="px-3 py-3 text-gray-400 hover:text-gray-700">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                    </div>
                ) : (
                    <button onClick={() => setAdding(true)}
                            className="w-full px-4 py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400 hover:border-orange-300 hover:text-orange-500 transition-all flex items-center justify-center gap-2">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        Add another website
                    </button>
                )}
            </div>

            <p className="text-xs text-gray-400 mb-8 flex items-center gap-1.5">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                You can always add more websites later from your dashboard.
            </p>

            <div className="flex gap-3">
                <button onClick={onBack} className="px-5 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-all">Back</button>
                <button onClick={onNext} disabled={data.domains.length === 0}
                        className={cn("flex-1 py-3 rounded-xl text-white font-semibold text-sm transition-all",
                            data.domains.length > 0 ? "bg-orange-500 hover:bg-orange-600 active:scale-95" : "bg-gray-200 text-gray-400 cursor-not-allowed")}>
                    Continue
                </button>
            </div>
        </div>
    );
}

// ── STEP: Goals ────────────────────────────────────────────────────────────────
function StepGoals({ data, setData, onNext, onBack }) {
    const ok = (data.goals||[]).length > 0 && data.timeline;
    return (
        <div>
            <div className="mb-8">
                <div className="text-orange-500 text-sm font-semibold mb-2">Step 3 of 5</div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{fontFamily:"'Georgia',serif"}}>What does success look like?</h2>
                <p className="text-gray-500 text-sm">We'll customize your strategy and reporting around what matters most to you.</p>
            </div>

            <div className="mb-6">
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-3">Your primary goals <span className="text-orange-400">(pick all that apply)</span></label>
                <CardSelect
                    options={[
                        { id:"traffic", icon:"📈", label:"More organic traffic", desc:"Grow visitors from search" },
                        { id:"rank", icon:"🏆", label:"Rank #1 on Google", desc:"Own your target keywords" },
                        { id:"leads", icon:"🧲", label:"Generate more leads", desc:"Turn visitors into clients" },
                        { id:"authority", icon:"✍️", label:"Build domain authority", desc:"Become the go-to source" },
                        { id:"local", icon:"📍", label:"Local SEO", desc:"Dominate your city or region" },
                        { id:"content", icon:"📝", label:"Content strategy", desc:"Publish content that ranks" },
                    ]}
                    value={data.goals}
                    onChange={v => setData({...data, goals:v})}
                    multi
                />
            </div>

            <div className="mb-8">
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-3">When do you want to see results?</label>
                <PillSelect
                    options={["3 months","6 months","12 months","Ongoing / No rush"]}
                    value={data.timeline}
                    onChange={v => setData({...data, timeline:v})}
                />
            </div>

            <div className="flex gap-3">
                <button onClick={onBack} className="px-5 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-all">Back</button>
                <button onClick={onNext} disabled={!ok}
                        className={cn("flex-1 py-3 rounded-xl text-white font-semibold text-sm transition-all",
                            ok ? "bg-orange-500 hover:bg-orange-600 active:scale-95" : "bg-gray-200 text-gray-400 cursor-not-allowed")}>
                    Continue
                </button>
            </div>
        </div>
    );
}

// ── STEP: Plan ─────────────────────────────────────────────────────────────────
const PRICE_DISPLAY = { launch: "$997", growth: "$1,997", "market-leader": "$3,800" };
const PLAN_TAG = { launch: null, growth: "Most popular", "market-leader": "Full service" };

function StepPlan({ data, setData, onNext, onBack }) {
    return (
        <div>
            <div className="mb-8">
                <div className="text-orange-500 text-sm font-semibold mb-2">Step 4 of 5</div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{fontFamily:"'Georgia',serif"}}>Choose your plan.</h2>
                <p className="text-gray-500 text-sm">You can upgrade or downgrade at any time. No contracts, ever.</p>
            </div>

            <div className="space-y-3 mb-8">
                {tiers.map(tier => {
                    const selected = data.plan === tier.key;
                    const tag = PLAN_TAG[tier.key];
                    return (
                        <button key={tier.key} onClick={() => setData({...data, plan: tier.key})}
                                className={cn(
                                    "w-full text-left rounded-2xl border-2 p-4 transition-all duration-200",
                                    selected ? "border-orange-500 bg-orange-50" : "bg-white border-gray-200 hover:border-orange-200"
                                )}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={cn("font-bold text-base", selected ? "text-orange-700" : "text-gray-900")}>{tier.name}</span>
                                        {tag && <span className={cn("text-[11px] font-bold px-2 py-0.5 rounded-full", selected ? "bg-orange-200 text-orange-700" : "bg-gray-100 text-gray-500")}>{tag}</span>}
                                    </div>
                                    <p className={cn("text-xs mb-1.5", selected ? "text-orange-600" : "text-gray-400")}>{tier.description}</p>
                                    <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                                        {tier.highlights.slice(0, 3).map(h => (
                                            <span key={h.description} className="text-xs text-gray-500 flex items-center gap-1">
                                                <span className={cn("w-1 h-1 rounded-full inline-block flex-shrink-0", selected ? "bg-orange-400" : "bg-gray-300")}></span>
                                                {h.description}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <div className={cn("text-xl font-bold", selected ? "text-orange-600" : "text-gray-900")}>{PRICE_DISPLAY[tier.key]}</div>
                                    <div className="text-xs text-gray-400">/mo</div>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="flex gap-3">
                <button onClick={onBack} className="px-5 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-all">Back</button>
                <button onClick={onNext} disabled={!data.plan}
                        className={cn("flex-1 py-3 rounded-xl text-white font-semibold text-sm transition-all",
                            data.plan ? "bg-orange-500 hover:bg-orange-600 active:scale-95" : "bg-gray-200 text-gray-400 cursor-not-allowed")}>
                    Continue
                </button>
            </div>
        </div>
    );
}

// ── STEP: Team ─────────────────────────────────────────────────────────────────
function StepTeam({ data, setData, onNext, onBack, submitting, submitError }) {
    const [invite, setInvite] = useState("");
    const [role, setRole] = useState("Viewer");

    const addInvite = () => {
        if (invite && invite.includes("@")) {
            setData({...data, invites: [...(data.invites||[]), {email:invite, role}]});
            setInvite("");
        }
    };

    const removeInvite = (i) => setData({...data, invites: data.invites.filter((_,idx) => idx !== i)});

    return (
        <div>
            <div className="mb-8">
                <div className="text-orange-500 text-sm font-semibold mb-2">Step 5 of 5</div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{fontFamily:"'Georgia',serif"}}>Invite your team.</h2>
                <p className="text-gray-500 text-sm">Give your colleagues or clients access to their dashboard. You can always do this later.</p>
            </div>

            {/* Invite row */}
            <div className="flex gap-2 mb-4">
                <input value={invite} onChange={e => setInvite(e.target.value)}
                       onKeyDown={e => e.key === "Enter" && addInvite()}
                       placeholder="colleague@company.com"
                       className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"/>
                <select value={role} onChange={e => setRole(e.target.value)}
                        className="border border-gray-200 rounded-xl px-3 py-3 text-sm text-gray-700 outline-none focus:border-orange-400 bg-white">
                    <option>Viewer</option>
                    <option>Editor</option>
                    <option>Admin</option>
                </select>
                <button onClick={addInvite} disabled={!invite || !invite.includes("@")}
                        className="px-4 py-3 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 disabled:bg-gray-100 disabled:text-gray-300 transition-all">
                    Invite
                </button>
            </div>

            {/* Invitees */}
            {(data.invites||[]).length > 0 && (
                <div className="space-y-2 mb-4">
                    {(data.invites||[]).map((inv, i) => (
                        <div key={i} className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
                            <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-xs">
                                {inv.email[0].toUpperCase()}
                            </div>
                            <span className="text-sm text-gray-700 flex-1">{inv.email}</span>
                            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{inv.role}</span>
                            <button onClick={() => removeInvite(i)} className="text-gray-300 hover:text-red-400 transition-colors">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Role legend */}
            <div className="bg-gray-50 rounded-xl p-4 mb-8 space-y-2">
                {[
                    { role:"Viewer", desc:"Can view reports and data only" },
                    { role:"Editor", desc:"Can update settings and tags" },
                    { role:"Admin", desc:"Full access, can invite others" },
                ].map(r => (
                    <div key={r.role} className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="font-semibold text-gray-700 w-12">{r.role}</span>
                        <span>{r.desc}</span>
                    </div>
                ))}
            </div>

            {/*{submitError && (*/}
            {/*    <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">*/}
            {/*        {submitError}*/}
            {/*    </div>*/}
            {/*)}*/}

            <div className="flex gap-3">
                <button onClick={onBack} disabled={submitting} className="px-5 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-all disabled:opacity-50">Back</button>
                <button onClick={onNext} disabled={submitting}
                        className="flex-1 py-3 rounded-xl text-white font-semibold text-sm bg-orange-500 hover:bg-orange-600 active:scale-95 transition-all disabled:bg-orange-300">
                    {submitting ? "Submitting…" : (data.invites||[]).length > 0 ? "Send invites & continue" : "Skip for now"}
                </button>
            </div>
        </div>
    );
}

// ── STEP: Ready ────────────────────────────────────────────────────────────────
function StepReady({ data, onLaunch }) {
    const [progress, setProgress] = useState(0);
    const [phase, setPhase] = useState(0);
    const phases = [
        "Setting up your account…",
        `Connecting ${data.domains?.[0] || "your website"}…`,
        "Running your first SEO audit…",
        "Preparing your dashboard…",
        "You're all set! 🎉",
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setProgress(p => {
                if (p >= 100) { clearInterval(interval); return 100; }
                return p + 2;
            });
        }, 60);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        setPhase(Math.min(Math.floor(progress / 25), phases.length - 1));
    }, [progress]);

    const done = progress >= 100;

    return (
        <div className="flex flex-col items-center text-center">
            {/* Big animated checkmark / spinner */}
            <div className="relative w-24 h-24 mb-8">
                <svg className="w-24 h-24 -rotate-90" viewBox="0 0 96 96">
                    <circle cx="48" cy="48" r="40" fill="none" stroke="#f3f4f6" strokeWidth="6"/>
                    <circle cx="48" cy="48" r="40" fill="none" stroke="#f97316" strokeWidth="6"
                            strokeLinecap="round"
                            strokeDasharray={`${2 * Math.PI * 40}`}
                            strokeDashoffset={`${2 * Math.PI * 40 * (1 - progress/100)}`}
                            style={{transition:"stroke-dashoffset 0.2s ease"}}
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    {done ? (
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                    ) : (
                        <span className="text-orange-500 font-bold text-lg">{progress}%</span>
                    )}
                </div>
            </div>

            <div className="h-6 mb-2">
                <p className="text-sm text-gray-500 transition-all duration-300">{phases[phase]}</p>
            </div>

            {!done ? (
                <>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3" style={{fontFamily:"'Georgia',serif"}}>
                        Getting everything ready…
                    </h2>
                    <p className="text-gray-400 text-sm max-w-xs">Hold tight — we're setting up your account and running your first audit in the background.</p>
                </>
            ) : (
                <>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3" style={{fontFamily: "'Georgia',serif"}}>
                        {data.firstName ? `Welcome, ${data.firstName}!` : "Welcome aboard!"}
                    </h2>
                    <p className="text-gray-500 text-sm max-w-xs mb-2">
                        Your account is live. Your first SEO audit
                        for <strong>{data.domains?.[0] || "your website"}</strong> is already running.
                    </p>

                    {/* Summary pills */}
                    <div className="flex flex-wrap justify-center gap-2 mb-8 mt-4">
                        {[
                            data.domains?.length > 0 && `${data.domains.length} site${data.domains.length > 1 ? "s" : ""}`,
                            data.plan && `${tiers.find(t => t.key === data.plan)?.name ?? data.plan} plan`,
                            (data.goals || []).length > 0 && `${(data.goals || []).length} goal${data.goals.length > 1 ? "s" : ""}`,
                        ].filter(Boolean).map(p => (
                            <span key={p}
                                  className="bg-orange-50 text-orange-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-orange-100">{p}</span>
                        ))}
                    </div>

                    <button onClick={() => {
                        window.open("/dashboard", "_self")
                    }}
                            className="w-full max-w-xs py-4 rounded-2xl bg-orange-500 text-white font-semibold text-base hover:bg-orange-600 active:scale-95 transition-all shadow-lg shadow-orange-200">
                        Go to my dashboard →
                    </button>
                    <p className="text-xs text-gray-400 mt-3">Your SEO strategist will reach out within 24 hours.</p>
                </>
            )}
        </div>
    );
}


// ── Shell layout ───────────────────────────────────────────────────────────────
function Shell({ step, data, children }) {
    const isWelcome = step === "welcome";
    const isReady = step === "ready";

    return (
        <div className="min-h-screen flex bg-gray-50">

            {/* Right content area */}
            <div className={cn(
                "flex-1 flex flex-col items-center justify-center p-6 md:p-12",
                (isWelcome || isReady) && "bg-white"
            )}>
                {/* Progress */}
                {!isWelcome && !isReady && (
                    <div className="w-full max-w-md mb-8 flex items-center justify-between">
                        <ProgressDots step={step}/>
                        <span className="text-xs text-gray-400">
              {["about","website","goals","plan","team"].indexOf(step) + 1} of 5
            </span>
                    </div>
                )}

                <div className={cn(
                    "w-full",
                    isWelcome || isReady ? "max-w-sm" : "max-w-md bg-white rounded-3xl shadow-sm border border-gray-100 p-8"
                )}>
                    {children}
                </div>
            </div>
        </div>
    );
}

// ── App root ───────────────────────────────────────────────────────────────────
export default function OnboardingComponent() {
    const [step, setStep] = useState("welcome");
    const [showDashboard, setShowDashboard] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [data, setData] = useState({
        firstName:"", lastName:"", email:"", biz:"", industry:"",
        domains:[], goals:[], timeline:"", plan:"", invites:[],
    });

    const { clientID } = useContext(DashboardContext);

    const go = (s) => {
        setStep(s);
        window.scrollTo?.(0,0);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setSubmitError(null);
        try {
            const tempData = JSON.stringify({
                client_id: clientID,
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                biz: data.biz,
                industry: data.industry,
                domains: data.domains,
                goals: data.goals,
                invites: data.invites,
                plan: data.plan,
                timeline: data.timeline,
            })
            sendSlackMessageTracked(`🇺🇸Money Incoming! Business: HerStory. \nClient Data: ${tempData}`, "#high-alerts")

            const res = await fetch("https://server.pabloo.com/website/onboarding_client/herstory", {
                method: "POST",
                headers: { "accept": "application/json", "Content-Type": "application/json" },
                body: tempData,
            });
            if (!res.ok) throw new Error(`Server responded with ${res.status}`);
            go("ready");
        } catch (err) {
            setSubmitError(err.message || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    // if (showDashboard) return <MockDashboard data={data}/>;

    const steps = {
        welcome: <StepWelcome onNext={() => go("about")}/>,
        about: <StepAbout data={data} setData={setData} onNext={() => go("website")} onBack={() => go("welcome")}/>,
        website: <StepWebsite data={data} setData={setData} onNext={() => go("goals")} onBack={() => go("about")}/>,
        goals: <StepGoals data={data} setData={setData} onNext={() => go("plan")} onBack={() => go("website")}/>,
        plan: <StepPlan data={data} setData={setData} onNext={() => go("team")} onBack={() => go("goals")}/>,
        team: <StepTeam data={data} setData={setData} onNext={handleSubmit} onBack={() => go("plan")} submitting={submitting} submitError={submitError}/>,
        ready: <StepReady data={data} onLaunch={() => setShowDashboard(true)}/>,
    };

    return (
        <Shell step={step} data={data}>
            {steps[step]}
        </Shell>
    );
}